
import React, { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContext";
import { isValidIban } from "@/utils/validators";
import { inferirTipoPersona, TipoPersona } from "@shared/personaFiscal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PaymentMethod, Tax, FreelanceInfo } from "@shared/types";

export default function Configuracion() {
  return (
    <div>
      <h1 className="text-3xl font-bold tracking-tighter">Configuración</h1>
      <p className="text-muted-foreground">Gestiona tus datos de facturación, impuestos y métodos de pago.</p>
      <Tabs defaultValue="usuario" className="mt-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="usuario">Mis Datos</TabsTrigger>
          <TabsTrigger value="impuestos">Impuestos</TabsTrigger>
          <TabsTrigger value="pagos">Métodos de Pago</TabsTrigger>
        </TabsList>
        <TabsContent value="usuario"><MisDatos /></TabsContent>
        <TabsContent value="impuestos"><Impuestos /></TabsContent>
        <TabsContent value="pagos"><MetodosPago /></TabsContent>
      </Tabs>
    </div>
  );
}

function MisDatos() {
    const { userProfile, updateProfile, isLoading, fetchWithAuth } = useUser();
    const [form, setForm] = useState<Partial<FreelanceInfo>>({});
    const [uploadingLogo, setUploadingLogo] = useState(false);

    useEffect(() => {
        if (userProfile) {
            setForm(userProfile);
        }
    }, [userProfile]);

    const handleFieldChange = (field: keyof FreelanceInfo, value: any) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validar tamaño (máximo 2MB)
        if (file.size > 2 * 1024 * 1024) {
            toast.error('El logo no debe superar 2MB');
            return;
        }

        // Validar tipo
        if (!file.type.startsWith('image/')) {
            toast.error('El archivo debe ser una imagen');
            return;
        }

        try {
            setUploadingLogo(true);
            const reader = new FileReader();
            reader.onload = async (event) => {
                const base64Data = event.target?.result as string;

                // Guardar directamente como data URL
                handleFieldChange('logoUrl', base64Data);
                toast.success('Logo cargado correctamente');
            };
            reader.readAsDataURL(file);
        } catch (error) {
            toast.error('Error al cargar el logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const handleRemoveLogo = () => {
        handleFieldChange('logoUrl', '');
    };

    // Tipo de persona: el guardado en el perfil; si no hay, el que deduce el NIF
    // (DNI/NIE → física, CIF → jurídica); y si tampoco, física.
    const tipoPersona: TipoPersona = form.tipoPersona ?? inferirTipoPersona(form.nif) ?? "F";

    const handleSave = async () => {
        if (form.iban && !isValidIban(form.iban)) {
            toast.error("IBAN no válido");
            return;
        }
        // Facturae describe a una persona física con <Individual>: necesita
        // nombre y primer apellido (el segundo es opcional).
        if (tipoPersona === "F" && (!form.nombrePila?.trim() || !form.primerApellido?.trim())) {
            toast.error("Completa tu nombre y primer apellido", { description: "Son obligatorios para emitir el Facturae como persona física." });
            return;
        }
        try {
            await updateProfile({ ...form, tipoPersona });
            toast.success("Datos guardados correctamente");
        } catch (error) {
            toast.error("Error al guardar", { description: "No se pudieron guardar los datos." });
        }
    };

    if (isLoading) return <p>Cargando tus datos...</p>;

    return (
        <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="tipo-persona">Tipo de persona</Label>
                    <Select value={tipoPersona} onValueChange={(value) => handleFieldChange('tipoPersona', value as TipoPersona)}>
                        <SelectTrigger id="tipo-persona">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="F">Persona física</SelectItem>
                            <SelectItem value="J">Persona jurídica</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                {tipoPersona === "F" && (
                    <>
                        <div className="grid gap-4 sm:grid-cols-3">
                            <div className="space-y-2">
                                <Label htmlFor="nombre-pila">Nombre</Label>
                                <Input id="nombre-pila" value={form.nombrePila || ''} onChange={e => handleFieldChange('nombrePila', e.target.value)} maxLength={40} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="primer-apellido">Primer apellido</Label>
                                <Input id="primer-apellido" value={form.primerApellido || ''} onChange={e => handleFieldChange('primerApellido', e.target.value)} maxLength={40} />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="segundo-apellido">Segundo apellido</Label>
                                <Input id="segundo-apellido" value={form.segundoApellido || ''} onChange={e => handleFieldChange('segundoApellido', e.target.value)} maxLength={40} />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground">Facturae identifica a una persona física con su nombre y apellidos, tal como figuran en tu DNI.</p>
                    </>
                )}
                 <div className="space-y-2">
                    <Label htmlFor="nombre-razon-social">Nombre o Razón Social</Label>
                    <Input id="nombre-razon-social" value={form.nombre || ''} onChange={e => handleFieldChange('nombre', e.target.value)} />
                </div>
                <div className="space-y-2">
                    <Label>Marca o Nombre Comercial (Opcional)</Label>
                    <Input value={form.nombreComercial || ''} onChange={e => handleFieldChange('nombreComercial', e.target.value)} placeholder="Tu marca o nombre comercial" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                        <Label>NIF/CIF</Label>
                        <Input value={form.nif || ''} onChange={e => handleFieldChange('nif', e.target.value.toUpperCase())} />
                    </div>
                    <div className="space-y-2">
                        <Label>IRPF (%)</Label>
                        <Input type="number" value={(form.irpfRate || 0) * 100} onChange={e => handleFieldChange('irpfRate', parseFloat(e.target.value) / 100)} />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>Dirección</Label>
                    <Input value={form.direccion || ''} onChange={e => handleFieldChange('direccion', e.target.value)} />
                </div>
                <div className="grid gap-4 sm:grid-cols-3">
                    <div className="space-y-2">
                        <Label>Código Postal</Label>
                        <Input value={form.codigoPostal || ''} onChange={e => handleFieldChange('codigoPostal', e.target.value)} placeholder="Ej. 28013" />
                    </div>
                    <div className="space-y-2">
                        <Label>Municipio</Label>
                        <Input value={form.ciudad || ''} onChange={e => handleFieldChange('ciudad', e.target.value)} placeholder="Ej. Madrid" />
                    </div>
                    <div className="space-y-2">
                        <Label>Provincia</Label>
                        <Input value={form.provincia || ''} onChange={e => handleFieldChange('provincia', e.target.value)} placeholder="Ej. Madrid" />
                    </div>
                </div>
                 <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                        <Label>Email</Label>
                        <Input type="email" value={form.email || ''} onChange={e => handleFieldChange('email', e.target.value)} />
                    </div>
                    <div className="space-y-2">
                        <Label>Teléfono</Label>
                        <Input value={form.telefono || ''} onChange={e => handleFieldChange('telefono', e.target.value)} />
                    </div>
                </div>
                <div className="space-y-2">
                    <Label>IBAN de cobro principal</Label>
                    <Input value={form.iban || ''} onChange={e => handleFieldChange('iban', e.target.value.replace(/\s+/g, "").toUpperCase())} />
                </div>
            </div>
            <div className="space-y-4">
                <h3 className="text-lg font-semibold">Series de Documentos</h3>
                <p className="text-sm text-muted-foreground">Prefijos y numeración de cada tipo de documento</p>

                <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Serie de Facturas</Label>
                            <Input
                                value={form.invoiceSeries || ''}
                                onChange={e => handleFieldChange('invoiceSeries', e.target.value)}
                                placeholder={`Ej: ${new Date().getFullYear()}`}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Número Inicial</Label>
                            <Input
                                type="number"
                                min="1"
                                value={form.invoiceNextNumber || 1}
                                onChange={e => handleFieldChange('invoiceNextNumber', parseInt(e.target.value) || 1)}
                            />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Vista previa: {form.invoiceSeries || new Date().getFullYear()}-{String(form.invoiceNextNumber || 1).padStart(4, '0')}</p>
                </div>

                <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Serie de Presupuestos</Label>
                            <Input
                                value={form.quoteSeries || ''}
                                onChange={e => handleFieldChange('quoteSeries', e.target.value)}
                                placeholder={`Ej: P${new Date().getFullYear()}`}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Número Inicial</Label>
                            <Input
                                type="number"
                                min="1"
                                value={form.quoteNextNumber || 1}
                                onChange={e => handleFieldChange('quoteNextNumber', parseInt(e.target.value) || 1)}
                            />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Vista previa: {form.quoteSeries || `P${new Date().getFullYear()}`}-{String(form.quoteNextNumber || 1).padStart(4, '0')}</p>
                </div>

                <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>Serie de Facturas Rectificativas</Label>
                            <Input
                                value={form.creditNoteSeries || ''}
                                onChange={e => handleFieldChange('creditNoteSeries', e.target.value)}
                                placeholder={`Ej: R${new Date().getFullYear()}`}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Número Inicial</Label>
                            <Input
                                type="number"
                                min="1"
                                value={form.creditNoteNextNumber || 1}
                                onChange={e => handleFieldChange('creditNoteNextNumber', parseInt(e.target.value) || 1)}
                            />
                        </div>
                    </div>
                    <p className="text-xs text-muted-foreground">Vista previa: {form.creditNoteSeries || `R${new Date().getFullYear()}`}-{String(form.creditNoteNextNumber || 1).padStart(4, '0')}</p>
                </div>

                <h3 className="text-lg font-semibold mt-6">Personalización de Facturas</h3>
                <p className="text-sm text-muted-foreground">Contenido adicional para tus documentos</p>

                <div className="space-y-2">
                    <Label>Logo de la Empresa</Label>
                    {form.logoUrl ? (
                        <div className="border rounded-lg p-4 space-y-2">
                            <img src={form.logoUrl} alt="Logo" className="max-h-24 object-contain" />
                            <Button variant="outline" size="sm" onClick={handleRemoveLogo}>
                                <X className="h-4 w-4 mr-2" />
                                Eliminar Logo
                            </Button>
                        </div>
                    ) : (
                        <div className="border-2 border-dashed rounded-lg p-6 text-center">
                            <Input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                className="hidden"
                                id="logo-upload"
                            />
                            <Label htmlFor="logo-upload" className="cursor-pointer">
                                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                                <p className="text-sm text-muted-foreground">
                                    {uploadingLogo ? 'Cargando...' : 'Click para subir logo (máx 2MB)'}
                                </p>
                            </Label>
                        </div>
                    )}
                </div>

                <div className="space-y-2">
                    <Label>Observaciones (Opcional)</Label>
                    <Textarea
                        value={form.observaciones || ''}
                        onChange={e => handleFieldChange('observaciones', e.target.value)}
                        placeholder="Observaciones que aparecerán en todas tus facturas"
                        rows={3}
                    />
                    <p className="text-xs text-muted-foreground">Aparecerá en el cuerpo de la factura</p>
                </div>

                <div className="space-y-2">
                    <Label>Pie de Página (Opcional)</Label>
                    <Textarea
                        value={form.footer || ''}
                        onChange={e => handleFieldChange('footer', e.target.value)}
                        placeholder="Texto que aparecerá al final de todas tus facturas"
                        rows={2}
                    />
                    <p className="text-xs text-muted-foreground">Aparecerá en el pie de todas las facturas</p>
                </div>

                <Button onClick={handleSave}>Guardar Cambios</Button>
            </div>
        </div>
    );
}


function TaxForm({ tax, onSave, onCancel }: { tax: Partial<Tax>, onSave: (tax: Partial<Tax>) => void, onCancel: () => void }) {
    const [currentTax, setCurrentTax] = useState(tax);

    useEffect(() => setCurrentTax(tax), [tax]);

    const handleChange = (field: keyof Tax, value: any) => {
        setCurrentTax(prev => ({...prev, [field]: value}));
    }

    return (
        <div className="grid gap-4 py-4">
            <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={currentTax.nombre || ''} onChange={(e) => handleChange('nombre', e.target.value)} placeholder="Ej: IVA General" />
            </div>
            <div className="space-y-2">
                <Label>Tipo (%)</Label>
                <Input type="number" value={(currentTax.rate || 0) * 100} onChange={(e) => handleChange('rate', parseFloat(e.target.value) / 100)} />
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onCancel}>Cancelar</Button>
                <Button onClick={() => onSave(currentTax)}>Guardar</Button>
            </DialogFooter>
        </div>
    );
}

function Impuestos() {
    const { fetchWithAuth } = useUser();
    const [taxes, setTaxes] = useState<Tax[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingTax, setEditingTax] = useState<Partial<Tax> | null>(null);

    const fetchTaxes = async () => {
        try {
            setIsLoading(true);
            const res = await fetchWithAuth('/impuestos');
            if(!res.ok) throw new Error('Error al cargar');
            setTaxes(await res.json());
        } catch (error) {
            toast.error('No se pudieron cargar los impuestos.');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => { fetchTaxes() }, []);

    const handleSave = async (tax: Partial<Tax>) => {
        const promise = fetchWithAuth(tax.id ? `/impuestos/${tax.id}` : '/impuestos', {
            method: tax.id ? 'PUT' : 'POST',
            body: JSON.stringify(tax)
        });

        toast.promise(promise, {
            loading: 'Guardando impuesto...',
            success: () => {
                fetchTaxes();
                setDialogOpen(false);
                return `Impuesto ${tax.id ? 'actualizado' : 'creado'}.`;
            },
            error: 'No se pudo guardar el impuesto.',
        });
    };

    const handleDelete = (id: string) => {
        const promise = fetchWithAuth(`/impuestos/${id}`, { method: 'DELETE' });
        toast.promise(promise, {
            loading: 'Eliminando impuesto...',
            success: () => { fetchTaxes(); return 'Impuesto eliminado.'; },
            error: 'No se pudo eliminar el impuesto.',
        });
    }

    const handleSetDefault = async (taxId: string) => {
        const promise = fetchWithAuth(`/impuestos/${taxId}`, {
            method: 'PUT',
            body: JSON.stringify({ isDefault: true })
        });

        toast.promise(promise, {
            loading: 'Actualizando impuesto por defecto...',
            success: () => {
                fetchTaxes();
                return 'Impuesto por defecto actualizado.';
            },
            error: 'No se pudo actualizar el impuesto por defecto.',
        });
    }

    const defaultTaxId = taxes.find(t => t.isDefault)?.id || '';

    return (
        <div className="mt-6">
            <div className="flex justify-between items-center mb-4">
                <p className="text-muted-foreground">Tipos de impuestos aplicables en tus facturas.</p>
                <Button onClick={() => { setEditingTax({ nombre: '', rate: 0.21, isDefault: false }); setDialogOpen(true); }}>Añadir Impuesto</Button>
            </div>
            {isLoading ? <p>Cargando...</p> : (
                 <div className="rounded-md border">
                    <RadioGroup value={defaultTaxId} onValueChange={handleSetDefault}>
                        <Table>
                            <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Tipo</TableHead><TableHead>Por Defecto</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {taxes.map(tax => (
                                    <TableRow key={tax.id}>
                                        <TableCell className="font-medium">{tax.nombre}</TableCell>
                                        <TableCell>{(tax.rate * 100).toFixed(2)}%</TableCell>
                                        <TableCell>
                                            <RadioGroupItem value={tax.id} id={`tax-${tax.id}`} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => { setEditingTax(tax); setDialogOpen(true); }}>Editar</Button>
                                            <Button variant="destructive" size="sm" onClick={() => handleDelete(tax.id)}>Eliminar</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </RadioGroup>
                </div>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editingTax?.id ? 'Editar' : 'Nuevo'} Impuesto</DialogTitle></DialogHeader>
                    {editingTax && <TaxForm tax={editingTax} onSave={handleSave} onCancel={() => setDialogOpen(false)} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function PaymentMethodForm({ method, onSave, onCancel }: { method: Partial<PaymentMethod>, onSave: (method: Partial<PaymentMethod>) => void, onCancel: () => void }) {
    const [currentMethod, setCurrentMethod] = useState(method);

    useEffect(() => setCurrentMethod(method), [method]);

    const handleChange = (field: keyof PaymentMethod, value: any) => {
        setCurrentMethod(prev => ({...prev, [field]: value}));
    }

    return (
        <div className="grid gap-4 py-4">
            <div className="space-y-2">
                <Label>Nombre</Label>
                <Input value={currentMethod.nombre || ''} onChange={(e) => handleChange('nombre', e.target.value)} placeholder="Ej: Transferencia Bancaria" />
            </div>
            <div className="space-y-2">
                <Label>IBAN (Opcional)</Label>
                <Input value={currentMethod.iban || ''} onChange={(e) => handleChange('iban', e.target.value)} placeholder="ES..." />
            </div>
            <DialogFooter>
                <Button variant="outline" onClick={onCancel}>Cancelar</Button>
                <Button onClick={() => onSave(currentMethod)}>Guardar</Button>
            </DialogFooter>
        </div>
    );
}

function MetodosPago() {
    const { fetchWithAuth } = useUser();
    const [methods, setMethods] = useState<PaymentMethod[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingMethod, setEditingMethod] = useState<Partial<PaymentMethod> | null>(null);

    const fetchMethods = async () => {
        try {
            setIsLoading(true);
            const res = await fetchWithAuth('/metodos-pago');
            if(!res.ok) throw new Error('Error al cargar');
            setMethods(await res.json());
        } catch (error) {
            toast.error('No se pudieron cargar los métodos de pago.');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => { fetchMethods() }, []);

    const handleSave = async (method: Partial<PaymentMethod>) => {
        const promise = fetchWithAuth(method.id ? `/metodos-pago/${method.id}` : '/metodos-pago', {
            method: method.id ? 'PUT' : 'POST',
            body: JSON.stringify(method)
        });

        toast.promise(promise, {
            loading: 'Guardando método de pago...',
            success: () => {
                fetchMethods();
                setDialogOpen(false);
                return `Método de pago ${method.id ? 'actualizado' : 'creado'}.`;
            },
            error: 'No se pudo guardar el método de pago.',
        });
    };

    const handleDelete = (id: string) => {
        const promise = fetchWithAuth(`/metodos-pago/${id}`, { method: 'DELETE' });
        toast.promise(promise, {
            loading: 'Eliminando método...',
            success: () => { fetchMethods(); return 'Método de pago eliminado.'; },
            error: 'No se pudo eliminar el método de pago.',
        });
    }

    const handleSetDefault = async (methodId: string) => {
        const promise = fetchWithAuth(`/metodos-pago/${methodId}`, {
            method: 'PUT',
            body: JSON.stringify({ isDefault: true })
        });

        toast.promise(promise, {
            loading: 'Actualizando método por defecto...',
            success: () => {
                fetchMethods();
                return 'Método de pago por defecto actualizado.';
            },
            error: 'No se pudo actualizar el método de pago por defecto.',
        });
    }

    const defaultMethodId = methods.find(m => m.isDefault)?.id || '';

    return (
        <div className="mt-6">
            <div className="flex justify-between items-center mb-4">
                <p className="text-muted-foreground">Opciones que ofreces a tus clientes para pagarte.</p>
                <Button onClick={() => { setEditingMethod({ nombre: '', iban: '', isDefault: false }); setDialogOpen(true); }}>Añadir Método</Button>
            </div>
            {isLoading ? <p>Cargando...</p> : (
                 <div className="rounded-md border">
                    <RadioGroup value={defaultMethodId} onValueChange={handleSetDefault}>
                        <Table>
                            <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>IBAN</TableHead><TableHead>Por Defecto</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {methods.map(method => (
                                    <TableRow key={method.id}>
                                        <TableCell className="font-medium">{method.nombre}</TableCell>
                                        <TableCell className="font-mono text-sm">{method.iban}</TableCell>
                                        <TableCell>
                                            <RadioGroupItem value={method.id} id={`method-${method.id}`} />
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button variant="ghost" size="sm" onClick={() => { setEditingMethod(method); setDialogOpen(true); }}>Editar</Button>
                                            <Button variant="destructive" size="sm" onClick={() => handleDelete(method.id)}>Eliminar</Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </RadioGroup>
                </div>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editingMethod?.id ? 'Editar' : 'Nuevo'} Método de Pago</DialogTitle></DialogHeader>
                    {editingMethod && <PaymentMethodForm method={editingMethod} onSave={handleSave} onCancel={() => setDialogOpen(false)} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
